import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Button, createStyles, Group, Loader, Stack, Text} from '@mantine/core';
import React, {useCallback, useEffect, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {SpreadsheetLocaleSelector} from 'bulk-import/panes/SpreadsheetLocaleSelector';
import UserConstants from 'constants/UserConstants';
import preferences from 'Preferences';
import useSpreadsheetUploadToS3 from 'spreadsheet-import-wizard/shared/useSpreadsheetUploadToS3';
import FileChooser from 'ui-library/FileChooser';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface SpreadsheetUploadResult {
  file: File;
  s3Url: string;
  headers: Array<string>;
}

export interface CustomFileHandler {
  shouldHandle: (file: File) => boolean;
  handle: (file: File) => Promise<{s3Url: string; headers: Array<string>}>;
}

export interface SpreadsheetUploadPaneProps {
  /** File extensions to accept (default: '.csv,.xls,.xlsx') */
  accept?: string;
  /** Heading text above the file chooser */
  title?: string;
  /** Handler for non-spreadsheet files. Files matching shouldHandle bypass the hook. */
  customFileHandler?: CustomFileHandler;
  /** Called when upload + parse completes successfully */
  onUploadComplete: (result: SpreadsheetUploadResult) => void;
  /** Called when the user removes a previously uploaded file */
  onRemove?: () => void;
  /** Description text shown below the locale selector label */
  localeDescription?: string;
  /** Existing file previously uploaded by the parent flow (for back-navigation persistence). */
  uploadedFile?: File | null;
  /** Existing parsed headers associated with uploadedFile. */
  uploadedHeaders?: Array<string> | null;
}

const useStyles = createStyles((theme) => ({
  errorIcon: {
    color: theme.colors.red[7],
  },
  successIcon: {
    color: theme.colors.green[7],
  },
}));

export default function SpreadsheetUploadPane({
  accept,
  title,
  customFileHandler,
  onUploadComplete,
  onRemove,
  localeDescription,
  uploadedFile,
  uploadedHeaders,
}: SpreadsheetUploadPaneProps): JSX.Element {
  const {classes} = useStyles();
  const {
    uploadFile: spreadsheetUploadFile,
    reset: spreadsheetReset,
    ...spreadsheetState
  } = useSpreadsheetUploadToS3();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [customUploading, setCustomUploading] = useState(false);
  const [customError, setCustomError] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [completedHeaders, setCompletedHeaders] = useState<Array<string> | null>(null);
  const [languageTag, setLanguageTag] = useState<string>(
    () =>
      preferences.get(UserConstants.SPREADSHEET_IMPORT_LANGUAGE_TAG) ||
      preferences.get(UserConstants.LANGUAGE_PREFERENCE_KEY)
  );

  const handleLocaleChange = useCallback((tag: string) => {
    preferences.set(UserConstants.SPREADSHEET_IMPORT_LANGUAGE_TAG, tag);
    setLanguageTag(tag);
  }, []);

  const startUpload = useCallback(
    (file: File) => {
      setCustomError(null);
      setUploadComplete(false);
      setCompletedHeaders(null);
      spreadsheetReset();

      if (customFileHandler?.shouldHandle(file)) {
        setCustomUploading(true);
        customFileHandler.handle(file).then(
          (result) => {
            setCustomUploading(false);
            setUploadComplete(true);
            setCompletedHeaders(result.headers);
            onUploadComplete({file, s3Url: result.s3Url, headers: result.headers});
          },
          (err) => {
            setCustomUploading(false);
            const message =
              (err as {userMessage?: string})?.userMessage ??
              (err instanceof Error ? err.message : 'Failed to process file.');
            setCustomError(message);
          }
        );
        return;
      }

      spreadsheetUploadFile(file);
    },
    [customFileHandler, onUploadComplete, spreadsheetReset, spreadsheetUploadFile]
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      setSelectedFile(file);
      startUpload(file);
    },
    [startUpload]
  );

  useEffect(() => {
    if (selectedFile && spreadsheetState.s3Url && spreadsheetState.headers) {
      setUploadComplete(true);
      setCompletedHeaders(spreadsheetState.headers);
      onUploadComplete({
        file: selectedFile,
        s3Url: spreadsheetState.s3Url,
        headers: spreadsheetState.headers,
      });
    }
  }, [selectedFile, spreadsheetState.s3Url, spreadsheetState.headers, onUploadComplete]);

  useEffect(() => {
    if (!uploadedFile) {
      return;
    }

    setSelectedFile((previousFile) => previousFile ?? uploadedFile);
    if (uploadedHeaders != null) {
      setUploadComplete(true);
      setCompletedHeaders((previousHeaders) => previousHeaders ?? uploadedHeaders);
    }
  }, [uploadedFile, uploadedHeaders]);

  const handleRemove = useCallback(() => {
    setSelectedFile(null);
    setUploadComplete(false);
    setCompletedHeaders(null);
    setCustomError(null);
    spreadsheetReset();
    onRemove?.();
  }, [spreadsheetReset, onRemove]);

  const isUploading = spreadsheetState.isUploading || customUploading;
  const error = spreadsheetState.error || customError;
  const showErrorState = error != null && !isUploading;

  const handleRetry = useCallback(() => {
    if (selectedFile == null) {
      return;
    }
    startUpload(selectedFile);
  }, [selectedFile, startUpload]);

  return (
    <Stack spacing="md">
      <Text weight={500}>{title ?? 'Upload a spreadsheet'}</Text>
      {selectedFile ? (
        <FileList>
          <FileRow
            file={selectedFile}
            onRequestRemove={handleRemove}
            disabled={isUploading}
            additionalContent={
              isUploading ? (
                <Loader
                  size="sm"
                  {...getTestProps({component: 'SpreadsheetUploadPane', element: 'fileRowLoader'})}
                />
              ) : showErrorState ? (
                <Group spacing="xs" noWrap={true}>
                  <BenchlingIcon
                    icon="error"
                    className={classes.errorIcon}
                    {...getTestProps({component: 'SpreadsheetUploadPane', element: 'fileRowErrorIcon'})}
                  />
                  <Text
                    size="xs"
                    color="red"
                    {...getTestProps({component: 'SpreadsheetUploadPane', element: 'fileRowErrorMessage'})}
                  >
                    {error}
                  </Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    color="red"
                    onClick={handleRetry}
                    {...getTestProps({component: 'SpreadsheetUploadPane', element: 'fileRowRetryButton'})}
                  >
                    Retry
                  </Button>
                </Group>
              ) : uploadComplete ? (
                <BenchlingIcon
                  icon="check"
                  className={classes.successIcon}
                  {...getTestProps({component: 'SpreadsheetUploadPane', element: 'fileRowSuccessIcon'})}
                />
              ) : undefined
            }
          />
        </FileList>
      ) : (
        <FileChooser accept={accept ?? '.csv,.xls,.xlsx'} multiple={false} onSelectFiles={handleFileSelect} />
      )}
      {!selectedFile && BenchlingConfig.NAV_ENABLE_LOCALE_AWARE_PARSING_FOR_SPREADSHEET_UPLOAD && (
        <SpreadsheetLocaleSelector
          languageTag={languageTag}
          onChange={handleLocaleChange}
          description={localeDescription}
        />
      )}
      {uploadComplete && selectedFile && <Text>{completedHeaders?.length ?? 0} columns detected</Text>}
      {error && (
        <Alert color="red" title="Upload error">
          {error}
        </Alert>
      )}
    </Stack>
  );
}
